import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useApolloClient } from 'react-apollo';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import debounce from 'debounce-promise';

import Spinner from '../global/Spinner';
import Button from '../global/Button';
import MultiSelect from '../global/MultiSelect';
import useAlbumFolderForFaceRecognitionDisabled from '../global/hooks/useAlbumFolderForFaceRecognitionDisabled';
import { AlbumFolderForFaceRecognitionDisabled } from '../../graphql/queries/album-folder';
import {
  addSelectedRosterIdentity,
  removeSelectedRosterIdentity,
  setSelectedRosterIdentities,
} from '../../actions/discover-album-folder';
import { ALBUM_FOLDER_ONBOARDING_TYPE } from '../../constants/album-folder';
import { NO_FR_JOIN_TRACKING } from '../../constants/discover-album-folder';
import { PRICING_CATEGORY_TYPE } from '../../constants/pricing-categories';
import { HIERARCHY_GRADES_ENUM_LABELS } from '../../constants/hierarchy-grades';
import {
  getSelectedRosterIdentitiesNameString,
  shouldDisplayBrandAttribution,
} from '../../util/discover-album-folder';
import { gtm } from '../../util/analytics';
import PhotographerAttributionBanner from './PhotographerAttributionBanner';
import WaldoSpeechBubble from '../global/WaldoSpeechBubble';
import OnboardingLayoutFooter from '../../apps/temp/views/onboarding/OnboardingLayout/components/OnboardingLayoutFooter';
import OnboardingLayoutScrollableContent from '../../apps/temp/views/onboarding/OnboardingLayout/components/OnboardingLayoutScrollableContent';
import DiscoverAlbumFolderNoFrJoinSelectedList from './DiscoverAlbumFolderNoFrJoinSelectedList';
import useNoFrJoinSubmit from './hooks/useNoFrJoinSubmit';

const propTypes = {
  albumFolderId: PropTypes.string,
};

const TAB = {
  SEARCH: 0,
  SELECTED: 1,
};

const isLinkedToAlbumFolder = (rosterIdentity, albumFolderId) =>
  (rosterIdentity?.links?.records ?? []).some(
    r => r.album_folder?.id === albumFolderId,
  );

const formatRosterIdentityOptionLabel = (option, context) => {
  if (!context?.inputValue) {
    return (
      <div className="w-100 d-flex justify-content-between align-items-center">
        {option.label}
        {option.sublabel && (
          <span className="text-sm text-muted ml-2">{option.sublabel}</span>
        )}
      </div>
    );
  }

  return (
    <div className="d-flex flex-column">
      {option.label}
      {option.sublabel && (
        <span className="text-sm text-muted">{option.sublabel}</span>
      )}
    </div>
  );
};

const DiscoverAlbumFolderNoFrJoin = ({ albumFolderId }) => {
  const { albumFolder, loading } = useAlbumFolderForFaceRecognitionDisabled({
    albumFolderId,
  });

  const apolloClient = useApolloClient();
  const dispatch = useDispatch();
  const selectedRosterIdentities = useSelector(
    state => state.discoverAlbumFolder.selectedRosterIdentities,
  );
  const { onJoin, isJoining } = useNoFrJoinSubmit({ albumFolderId });

  const [grade, setGrade] = useState('');
  const [tabIndex, setTabIndex] = useState(TAB.SEARCH);

  const selectedCount = selectedRosterIdentities?.length ?? 0;

  // The selection lives in redux and survives navigation, so drop anything
  // picked for a different event before it can be joined to this one.
  useEffect(() => {
    const linked = (selectedRosterIdentities ?? []).filter(rosterIdentity =>
      isLinkedToAlbumFolder(rosterIdentity, albumFolderId),
    );
    if (linked.length !== selectedCount) {
      dispatch(setSelectedRosterIdentities(linked));
    }
  }, [albumFolderId]);

  // The selected tab disappears with the last removal; land back on search.
  useEffect(() => {
    if (selectedCount === 0 && tabIndex !== TAB.SEARCH) {
      setTabIndex(TAB.SEARCH);
    }
  }, [selectedCount]);

  if (loading || !albumFolder) {
    return (
      <div className="text-center mt-5">
        <Spinner />
      </div>
    );
  }

  const grades = albumFolder.grades?.records ?? [];
  const isRefIdJoin =
    albumFolder.onboarding_type === ALBUM_FOLDER_ONBOARDING_TYPE.REF_ID;
  const isGradeJoin =
    albumFolder.onboarding_type === ALBUM_FOLDER_ONBOARDING_TYPE.GRADE;
  const isFullNameJoin =
    albumFolder.onboarding_type === ALBUM_FOLDER_ONBOARDING_TYPE.FULL_NAME;
  const hasGrades = grades.length > 0;
  // FULL_NAME searches by name alone. GRADE narrows by grade first, but falls
  // back to a name-only search when the event has no grades to pick from.
  const isNameOnlyJoin = isFullNameJoin || (isGradeJoin && !hasGrades);
  const isSchoolAndAcademics = (
    albumFolder.pricing_categories?.records ?? []
  ).some(
    r =>
      r?.pricing_category?.type === PRICING_CATEGORY_TYPE.SCHOOL_AND_ACADEMICS,
  );
  const subjectTerm = isSchoolAndAcademics ? 'Student' : 'Member';

  const rosterIdentityToOption = rosterIdentity => {
    const albumFolderLink = (rosterIdentity?.links?.records ?? []).find(
      r => r.album_folder?.id === albumFolderId,
    );
    const refIdSuffix = albumFolderLink?.ref_id
      ? `#***${albumFolderLink.ref_id.slice(-3)}`
      : '';

    return {
      label: `${rosterIdentity.first_name} ${rosterIdentity.last_name}`,
      sublabel: refIdSuffix,
      value: rosterIdentity.id,
      rosterIdentity,
    };
  };

  const searchRosterIdentities = async (query = '') => {
    const trimmed = query.trim();
    if (!trimmed) return [];

    const variables = {
      id: albumFolderId,
      include_roster_identity_data: true,
    };

    if (isRefIdJoin) {
      variables.exact_ref_id = trimmed;
    } else {
      const [exactFirstName, ...exactLastName] = trimmed.split(' ');
      if (!exactFirstName || !exactLastName?.length) {
        return [];
      }
      if (!isNameOnlyJoin) {
        variables.grade = grade;
      }
      variables.exact_first_name = exactFirstName;
      variables.exact_last_name = exactLastName.join(' ');
    }

    const { data: { albumFolder: albumFolderWithFoundRis } = {} } =
      await apolloClient.query({
        query: AlbumFolderForFaceRecognitionDisabled,
        variables,
        fetchPolicy: 'network-only',
      });

    const records = albumFolderWithFoundRis.roster_identities?.records ?? [];
    return records.map(rosterIdentityToOption);
  };

  const searchRosterIdentitiesDebounced = debounce(searchRosterIdentities, 500);

  // Picking an option adds it straight to the selection, clears the search
  // and shows the list so the pick is visibly confirmed.
  const onSelectOption = option => {
    if (!option?.rosterIdentity) return;
    // Already in the list: the reducer would ignore it, so don't count it.
    if (
      selectedRosterIdentities.some(({ id }) => id === option.rosterIdentity.id)
    ) {
      return;
    }

    dispatch(addSelectedRosterIdentity(option.rosterIdentity));
    setTabIndex(TAB.SELECTED);
    gtm.trackEvent({
      type: NO_FR_JOIN_TRACKING.TYPE,
      action: NO_FR_JOIN_TRACKING.ACTION.IDENTITY_SELECTED,
      label: albumFolderId,
      identity_count: selectedCount + 1,
    });
  };

  const onRemove = rosterIdentity => {
    dispatch(removeSelectedRosterIdentity(rosterIdentity));
    gtm.trackEvent({
      type: NO_FR_JOIN_TRACKING.TYPE,
      action: NO_FR_JOIN_TRACKING.ACTION.IDENTITY_REMOVED,
      label: albumFolderId,
      identity_count: selectedCount - 1,
    });
  };

  const canSearch = isRefIdJoin || isNameOnlyJoin || !!grade;
  const namesString = getSelectedRosterIdentitiesNameString(
    selectedRosterIdentities,
  );

  const searchForm = isRefIdJoin ? (
    <div className="w-100 mb-4">
      <label htmlFor="no-fr-student-id" className="text-uppercase small">
        What is their {subjectTerm} ID?
      </label>
      <MultiSelect
        id="no-fr-student-id"
        placeholder={`Enter ${subjectTerm} ID...`}
        value={null}
        defaultOptions={[]}
        loadOptions={searchRosterIdentitiesDebounced}
        getOptionValue={item => item.value}
        formatOptionLabel={formatRosterIdentityOptionLabel}
        noOptionsMessage={({ inputValue }) =>
          inputValue ? 'No matches found' : `Search by ${subjectTerm} ID...`
        }
        isClearable
        isDisabled={isJoining}
        onChange={onSelectOption}
      />
    </div>
  ) : (
    <Fragment>
      {!isNameOnlyJoin && (
        <div className="w-100 mb-4">
          <label htmlFor="no-fr-grade" className="text-uppercase small">
            What grade are they in?
          </label>
          <select
            id="no-fr-grade"
            className="form-control"
            value={grade}
            disabled={isJoining}
            onChange={e => setGrade(e.target.value)}
          >
            <option value="">Select Grade</option>
            {grades.map(({ id, grade: gradeValue }) => (
              <option key={id} value={gradeValue}>
                {HIERARCHY_GRADES_ENUM_LABELS[gradeValue] ?? gradeValue}
              </option>
            ))}
          </select>
        </div>
      )}
      {canSearch && (
        <div className="w-100 mb-4">
          <label htmlFor="no-fr-name" className="text-uppercase small">
            What is their name?
          </label>
          <MultiSelect
            id="no-fr-name"
            placeholder="Search name here..."
            value={null}
            defaultOptions={[]}
            loadOptions={searchRosterIdentitiesDebounced}
            getOptionValue={item => item.value}
            formatOptionLabel={formatRosterIdentityOptionLabel}
            noOptionsMessage={({ inputValue }) =>
              inputValue ? 'No matches found' : 'Search by full name...'
            }
            isClearable
            isDisabled={isJoining}
            onChange={onSelectOption}
          />
        </div>
      )}
    </Fragment>
  );

  return (
    <Fragment>
      {shouldDisplayBrandAttribution(albumFolder) && (
        <PhotographerAttributionBanner account={albumFolder.account} />
      )}
      <WaldoSpeechBubble position="right" className="mb-4">
        {selectedCount === 0
          ? 'Tell me who you want me to look for!'
          : `Got it! Add another ${subjectTerm.toLowerCase()} or view their photos below.`}
      </WaldoSpeechBubble>

      <OnboardingLayoutScrollableContent className="w-100">
        {selectedCount === 0 ? (
          searchForm
        ) : (
          <Fragment>
            <div className="no-fr-join-nav mb-4" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={tabIndex === TAB.SEARCH}
                className={classNames({ active: tabIndex === TAB.SEARCH })}
                onClick={() => setTabIndex(TAB.SEARCH)}
              >
                Find another {subjectTerm.toLowerCase()}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tabIndex === TAB.SELECTED}
                className={classNames({ active: tabIndex === TAB.SELECTED })}
                onClick={() => setTabIndex(TAB.SELECTED)}
              >
                Selected ({selectedCount})
              </button>
            </div>
            {tabIndex === TAB.SEARCH ? (
              searchForm
            ) : (
              <DiscoverAlbumFolderNoFrJoinSelectedList
                albumFolderId={albumFolderId}
                rosterIdentities={selectedRosterIdentities}
                onRemove={onRemove}
                disabled={isJoining}
              />
            )}
          </Fragment>
        )}
      </OnboardingLayoutScrollableContent>
      {selectedCount > 0 && (
        <OnboardingLayoutFooter>
          <Button
            btnSize="lg"
            className="mt-3"
            disabled={isJoining}
            onClick={() => onJoin(selectedRosterIdentities)}
          >
            {isJoining ? 'Joining...' : `View ${namesString}'s photos`}
          </Button>
        </OnboardingLayoutFooter>
      )}
    </Fragment>
  );
};

DiscoverAlbumFolderNoFrJoin.propTypes = propTypes;

export default DiscoverAlbumFolderNoFrJoin;
